"use client"

import { useForm, type SubmitHandler } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import type { components } from "umbraco"
import { postEggedPlusGeneralCareerSubmit } from "api"
import {
  FormShell,
  FormTextField,
  FormFileUpload,
  FormCheckboxField,
  FieldRow,
  FieldCol,
  useFormSubmit,
  checkBooleanResponse,
  validateRecipients,
  RecipientsConfigError,
  getShellLabels,
  type FormLanguage,
} from "../../common/forms"
import { createSchema, type FormValues, defaultValues } from "./formSchema"

type GeneralCareerFormData =
  components["schemas"]["GeneralCareerFormPropertiesModel"]

function getFieldLabels(locale: string) {
  if (locale === "en") {
    return {
      fullName: "Full Name",
      phone: "Phone",
      email: "Email",
      cv: "Attach CV",
    }
  }
  return {
    fullName: "שם מלא",
    phone: "טלפון",
    email: 'דוא"ל',
    cv: "צירוף קורות חיים",
  }
}

interface Props {
  data: GeneralCareerFormData
  settings?: Record<string, unknown> | null
  locale: string
  culture: string
  [key: string]: unknown
}

export default function GeneralCareerForm({ data, locale }: Props) {
  const shellLabels = getShellLabels(locale)
  const fieldLabels = getFieldLabels(locale)
  const recipients = data.recipients ?? []
  const showCheckbox = data.showCheckbox ?? false
  const checkboxRequired = showCheckbox && (data.checkboxRequired ?? false)

  const schema = createSchema(locale as FormLanguage, checkboxRequired)

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: "onBlur",
  })

  const cultureLocale =
    locale === "he" ? "he-IL" : locale === "en" ? "en-US" : locale

  const { state, isPending, submit, retry } = useFormSubmit<FormValues>({
    mutationFn: async (formData) => {
      const fd = new FormData()
      fd.append("FullName", formData.fullName)
      fd.append("Phone", formData.phone)
      fd.append("Email", formData.email)
      fd.append("ReferrerUrl", window.location.href)
      // EGDE-258: same fix as AccidentReportForm — the backend binds
      // ConfirmationCheckbox and emails it; append so the consent value isn't
      // dropped when the checkbox is enabled.
      // EGDE-445: only send it when the checkbox is actually shown — a hidden
      // checkbox must not arrive as "לא" in the email.
      if (showCheckbox) {
        fd.append("ConfirmationCheckbox", String(!!formData.checkbox))
      }
      recipients.forEach((r) => fd.append("Recipients", r))
      fd.append("Cv", formData.cvFile, formData.cvFile.name)
      return checkBooleanResponse(
        await postEggedPlusGeneralCareerSubmit(fd, undefined, cultureLocale),
      )
    },
    onReset: reset,
  })

  if (!validateRecipients(recipients)) {
    return <RecipientsConfigError formName="GeneralCareerForm" />
  }

  const onSubmit: SubmitHandler<FormValues> = (formData) => submit(formData)

  return (
    <FormShell
      title={data.title}
      subtitle={data.subtitle}
      labels={shellLabels}
      state={state}
      isPending={isPending}
      onSubmit={handleSubmit(onSubmit)}
      onRetry={retry}
      footer={
        showCheckbox ? (
          <FormCheckboxField
            control={control}
            name="checkbox"
            markup={data.checkboxText?.markup}
            required={checkboxRequired}
            error={errors.checkbox?.message}
          />
        ) : null
      }
    >
      <FieldRow>
        <FieldCol>
          <FormTextField
            control={control}
            name="fullName"
            label={fieldLabels.fullName}
            required
            autoComplete="name"
            error={errors.fullName?.message}
          />
        </FieldCol>
        <FieldCol>
          <FormTextField
            control={control}
            name="phone"
            label={fieldLabels.phone}
            required
            type="tel"
            autoComplete="tel"
            error={errors.phone?.message}
          />
        </FieldCol>
      </FieldRow>

      {/* Email + CV share one row (two columns) to match the design. */}
      <FieldRow>
        <FieldCol>
          <FormTextField
            control={control}
            name="email"
            label={fieldLabels.email}
            required
            type="email"
            autoComplete="email"
            error={errors.email?.message}
          />
        </FieldCol>
        <FieldCol>
          <FormFileUpload
            control={control}
            name="cvFile"
            label={fieldLabels.cv}
            accept=".pdf,.doc,.docx"
            required
            error={errors.cvFile?.message}
          />
        </FieldCol>
      </FieldRow>
    </FormShell>
  )
}
